/**
 * Google Analytics 4 Enhanced Event Tracking Utility
 * Provides type-safe wrapper functions for GA4 event tracking
 */

// Extend Window interface to include gtag
declare global {
    interface Window {
        gtag?: (
            command: 'config' | 'event' | 'js' | 'set',
            targetId: string | Date,
            config?: Record<string, unknown>
        ) => void;
        dataLayer?: unknown[];
    }
}

/**
 * Safely call gtag if it's available
 */
const trackEvent = (eventName: string, eventParams?: Record<string, unknown>): void => {
    if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', eventName, eventParams);
    }
};

/**
 * Analytics tracking functions
 */
export const Analytics = {
    /**
     * Track page view (called automatically by GA4, but can be used for SPA navigation)
     */
    trackPageView: (pageTitle: string, pagePath: string): void => {
        trackEvent('page_view', {
            page_title: pageTitle,
            page_path: pagePath,
        });
    },

    /**
     * Track navigation clicks
     */
    trackNavigation: (linkText: string, destination: string): void => {
        trackEvent('navigation_click', {
            link_text: linkText,
            destination: destination,
            event_category: 'Navigation',
        });
    },

    /**
     * Track contact form submission
     */
    trackContactFormSubmit: (success: boolean, errorMessage?: string): void => {
        trackEvent('contact_form_submit', {
            success: success,
            error_message: errorMessage || 'none',
            event_category: 'Contact',
        });
    },

    /**
     * Track contact form field interactions
     */
    trackFormFieldInteraction: (fieldName: string): void => {
        trackEvent('form_field_interaction', {
            field_name: fieldName,
            event_category: 'Contact',
        });
    },

    /**
     * Track project card clicks
     */
    trackProjectView: (projectName: string, action: 'view_details' | 'visit_live'): void => {
        trackEvent('project_interaction', {
            project_name: projectName,
            action: action,
            event_category: 'Projects',
        });
    },

    /**
     * Track external link clicks
     */
    trackExternalLink: (linkUrl: string, linkText: string, context: string): void => {
        trackEvent('external_link_click', {
            link_url: linkUrl,
            link_text: linkText,
            context: context,
            event_category: 'Outbound Links',
        });
    },

    /**
     * Track social media link clicks
     */
    trackSocialClick: (platform: string): void => {
        trackEvent('social_media_click', {
            platform: platform,
            event_category: 'Social Media',
        });
    },

    /**
     * Track scroll depth
     */
    trackScrollDepth: (depth: number): void => {
        trackEvent('scroll_depth', {
            depth_percentage: depth,
            event_category: 'Engagement',
        });
    },

    /**
     * Track section visibility
     */
    trackSectionView: (sectionName: string): void => {
        trackEvent('section_view', {
            section_name: sectionName,
            event_category: 'Engagement',
        });
    },

    /**
     * Track CTA button clicks
     */
    trackCTAClick: (ctaText: string, ctaLocation: string): void => {
        trackEvent('cta_click', {
            cta_text: ctaText,
            cta_location: ctaLocation,
            event_category: 'Engagement',
        });
    },

    /**
     * Track time spent on page
     */
    trackTimeOnPage: (seconds: number, section?: string): void => {
        trackEvent('time_on_page', {
            duration_seconds: seconds,
            section: section || 'overall',
            event_category: 'Engagement',
        });
    },

    /**
     * Track errors
     */
    trackError: (errorType: string, errorMessage: string, context: string): void => {
        trackEvent('error', {
            error_type: errorType,
            error_message: errorMessage,
            context: context,
            event_category: 'Errors',
        });
    },

    /**
     * Track file downloads (if applicable)
     */
    trackDownload: (fileName: string, fileType: string): void => {
        trackEvent('file_download', {
            file_name: fileName,
            file_type: fileType,
            event_category: 'Downloads',
        });
    },
};

/**
 * Initialize scroll depth tracking
 */
export const initScrollDepthTracking = (): void => {
    const scrollDepths = [25, 50, 75, 100];
    const triggered: Set<number> = new Set();

    const checkScrollDepth = (): void => {
        const scrollPercentage = Math.round(
            ((window.scrollY + window.innerHeight) / document.documentElement.scrollHeight) * 100
        );

        scrollDepths.forEach((depth) => {
            if (scrollPercentage >= depth && !triggered.has(depth)) {
                triggered.add(depth);
                Analytics.trackScrollDepth(depth);
            }
        });
    };

    window.addEventListener('scroll', checkScrollDepth, { passive: true });
};

/**
 * Initialize section view tracking with Intersection Observer
 */
export const initSectionTracking = (): void => {
    const sections = document.querySelectorAll('section[id]');
    const observedSections: Set<string> = new Set();

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && !observedSections.has(entry.target.id)) {
                    observedSections.add(entry.target.id);
                    Analytics.trackSectionView(entry.target.id);
                }
            });
        },
        { threshold: 0.5 } // Trigger when 50% of section is visible
    );

    sections.forEach((section) => observer.observe(section));
};

/**
 * Initialize time tracking
 */
export const initTimeTracking = (): void => {
    const startTime = Date.now();

    // Track time on page at intervals
    const intervals = [30, 60, 180, 300]; // 30s, 1min, 3min, 5min
    const triggered: Set<number> = new Set();

    setInterval(() => {
        const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);

        intervals.forEach((interval) => {
            if (elapsedSeconds >= interval && !triggered.has(interval)) {
                triggered.add(interval);
                Analytics.trackTimeOnPage(interval);
            }
        });
    }, 5000); // Check every 5 seconds

    // Track time on page before leaving
    window.addEventListener('beforeunload', () => {
        const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
        Analytics.trackTimeOnPage(elapsedSeconds);
    });
};
